/** Klang: title band, a dark band carrying the lead line, then the scroll
 *  photo beside two columns of text. */

import { FramedImage, OutlineButtonLink } from '../components/ui'
import type { ContentBlock, PageContent } from '../lib/api'
import { useApi, useDocumentMeta, useHreflang, useLocale } from '../lib/hooks'
import { pagePaths, pathFor, t } from '../lib/i18n'

function lines(text: string | undefined): string[] {
  return (text ?? '').split('\n').filter(Boolean)
}

export default function Klang() {
  const locale = useLocale()
  const strings = t(locale)
  const { data } = useApi<PageContent>(`/api/pages/klang?locale=${locale}`)

  useDocumentMeta(data?.title || strings.meta.klang.title, strings.meta.klang.description)
  useHreflang(pagePaths('klang'))

  const blocks: ContentBlock[] = data?.blocks ?? []
  const image = blocks.find((b) => b.type === 'image')
  const texts = blocks.filter((b) => b.type === 'text')

  // First text block: heading, then the lead line, then paragraphs.
  const col1 = lines(texts[0]?.text)
  const heading = col1[0] ?? strings.meta.klang.heading
  const lead = col1[1] ?? ''
  const col1Rest = col1.slice(2)
  const col2 = lines(texts[1]?.text)

  return (
    <>
      <section className="bg-paper px-6 pb-12 pt-16 md:px-10 md:pt-24" data-testid="klang-head">
        <div className="mx-auto max-w-[1200px]">
          <h1 className="m-0 font-serif text-[clamp(40px,6vw,64px)] font-medium leading-[1.05] text-ink">
            {heading}
          </h1>
        </div>
      </section>

      {lead ? (
        <section className="bg-night px-6 py-14 md:px-10 md:py-20" data-testid="klang-lead">
          <p
            className="mx-auto m-0 max-w-[1200px] font-serif text-[clamp(28px,4vw,44px)] italic leading-[1.25] text-paper"
            data-testid="klang-quote"
          >
            {lead}
          </p>
        </section>
      ) : null}

      <section className="bg-paper px-6 py-16 md:px-10 md:py-24" data-testid="klang-body">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-12 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] lg:gap-20">
          <div>
            {image?.src ? (
              <FramedImage
                filename={image.src.replace(/^\/media\//, '')}
                alt={image.alt || heading}
                loading="eager"
                sizes="(max-width: 1024px) 100vw, 400px"
              />
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-14">
            <div className="font-sans text-[18px] leading-[1.65] text-ink">
              {col1Rest.map((line, index) => (
                <p key={index} className="m-0 mb-[1.1em] last:mb-0">
                  {line}
                </p>
              ))}
            </div>
            <div className="font-sans text-[18px] leading-[1.65] text-ink">
              {col2.map((line, index) => (
                <p key={index} className="m-0 mb-[1.1em] last:mb-0">
                  {line}
                </p>
              ))}
              <div className="mt-10">
                <OutlineButtonLink to={pathFor(locale, 'galerie')}>{strings.nav.galerie}</OutlineButtonLink>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
